import { cn, extractTitle } from "@utils";
import { Button, Checkbox } from "@components";
import { CSVLink } from "react-csv";
import { Download } from "react-bootstrap-icons";
import { ClipLoader } from "react-spinners";
import { useMerchant } from "@auth";
import { useProgressiveRender } from "@hooks";

const ROW_HEIGHTS = {
  sm: "h-10",
  md: "h-14",
  lg: "h-16",
};

const SimpleTable = ({
  config,
  data,
  noPad = false,
  noFooter = false,
  radioTable,
  extraCol = false,
  enableExport = false,
  exportPosition = "bottom",
  rowHeight = "md",
  compact = false,
  // When true, disables internal scroll containers so a parent scroll
  // container can own scrolling and sticky headers work correctly.
  embedded = false,
  // Optional row selection controls
  isRowSelected,
  onToggleRow,
  isRowDisabled,
  selectionDisabled = false,
  selectionLoading = false,
  // Progressive rendering for large datasets
  lazyLoad = false,
  lazyLoadBatchSize = 50,
  // Infinite scroll – fetch more data from server as user scrolls
  onLoadMore,
  hasMoreData = false,
  totalRows,
  totalSelectedRows,
  // When a search narrows the displayed list, pass the search hit count here
  // so the header shows "displaying X accounts" and the select-all checkbox
  // reflects the visible subset rather than the full dataset.
  displayedRows,
  searchTerm,
  // Select-all override for when not all data is loaded in memory
  onSelectAll,
  onDeselectAll,
  // Optional element rendered beside the selection header label
  headerExtra,
}) => {
  const { hasPermission } = useMerchant();
  const rowHeightClass = ROW_HEIGHTS[rowHeight] || ROW_HEIGHTS.lg;

  const { visibleCount, sentinelRef, hasMore } = useProgressiveRender(
    data?.length || 0,
    {
      enabled: lazyLoad,
      batchSize: lazyLoadBatchSize,
      onLoadMore,
      hasMoreData,
    },
  );
  const visibleData = lazyLoad ? (data || []).slice(0, visibleCount) : data;

  const selectionEnabled = !!isRowSelected && !!onToggleRow;
  const isSearchActive = displayedRows != null;

  // When totalRows / totalSelectedRows overrides are provided the parent
  // already knows the full-dataset counts, so we can skip the expensive
  // per-row scans that only matter for purely in-memory datasets.
  // However, when a search is active the select-all checkbox must reflect
  // the visible subset, so we fall back to per-row checking.
  const hasOverrides =
    totalSelectedRows !== undefined && totalRows !== undefined;
  const needsPerRowCheck = isSearchActive || !hasOverrides;

  const eligibleRows =
    selectionEnabled && needsPerRowCheck && data && data.length > 0
      ? data.filter((row) => !(isRowDisabled && isRowDisabled(row)))
      : [];

  const allSelected = selectionEnabled
    ? needsPerRowCheck
      ? eligibleRows.length > 0 &&
        eligibleRows.every((row) => isRowSelected(row))
      : totalSelectedRows > 0 && totalSelectedRows === totalRows
    : false;

  const selectedCount = hasOverrides
    ? totalSelectedRows
    : selectionEnabled && eligibleRows.length > 0
      ? eligibleRows.reduce(
          (count, row) => (isRowSelected(row) ? count + 1 : count),
          0,
        )
      : 0;

  const headers = () =>
    config.columns
      .filter((x) => !!x.key)
      .map((x) => ({ label: x.title, key: x.key }));

  return (
    <div
      className={cn(
        "flex-1 flex flex-col h-full",
        !embedded && "overflow-auto",
      )}
    >
      {enableExport && exportPosition === "top" && data && data.length > 0 && (
        <div className="flex justify-end pb-3 border-b border-zinc-200">
          <CSVLink
            data={data}
            headers={headers()}
            filename={`${config.key || "export"}_${new Date().getTime()}.csv`}
            target="_blank"
          >
            <Button color="secondary">
              <Download className="mr-2" />
              Export
            </Button>
          </CSVLink>
        </div>
      )}
      {/* Table */}
      <div className={cn("w-full", !embedded && "overflow-auto")}>
        {selectionEnabled && (
          <div className="px-4 pb-2 pt-3 text-sm bg-white flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <span className="text-zinc-900 font-semibold flex items-center gap-2">
                Selected · {selectedCount} / {totalRows ?? eligibleRows.length}
                {selectionLoading && <ClipLoader size={12} color="#a1a1aa" />}
              </span>
              {headerExtra && <div className="ml-auto">{headerExtra}</div>}
            </div>
            {isSearchActive && (
              <span className="text-zinc-400 text-xs font-normal">
                Displaying {displayedRows} accounts matching "{searchTerm}"
              </span>
            )}
          </div>
        )}
        <table
          className="max-w-full w-full table-auto divide-y divide-zinc-300 "
          style={{ borderCollapse: "separate", borderSpacing: 0 }}
        >
          <thead>
            <tr>
              {selectionEnabled && (
                <th
                  className={cn(
                    "sticky top-0 z-10 bg-white px-3 font-semibold border-b border-zinc-200 text-left",
                    compact ? "py-2 text-xs" : "py-3.5 text-sm",
                  )}
                  style={{ width: 40, minWidth: 40, maxWidth: 40 }}
                >
                  <Checkbox
                    checked={allSelected}
                    disabled={
                      selectionDisabled ||
                      (needsPerRowCheck
                        ? eligibleRows.length === 0
                        : totalRows === 0)
                    }
                    onChange={(e) => {
                      if (selectionDisabled) return;
                      const next = e.target.checked;
                      if (next && onSelectAll) {
                        onSelectAll();
                      } else if (!next && onDeselectAll) {
                        onDeselectAll();
                      } else {
                        if (!eligibleRows || eligibleRows.length === 0) return;
                        if (next) {
                          eligibleRows.forEach((row) => {
                            if (!isRowSelected(row)) onToggleRow(row);
                          });
                        } else {
                          eligibleRows.forEach((row) => {
                            if (isRowSelected(row)) onToggleRow(row);
                          });
                        }
                      }
                    }}
                  />
                </th>
              )}
              {config.columns
                .filter((x) => !x.shouldShow || x.shouldShow())
                .map((x, i) => (
                  <th
                    key={i}
                    style={{
                      paddingLeft: noPad ? "0" : "",
                      paddingRight: noPad ? "0" : "",
                    }}
                    className={cn(
                      "sticky top-0 z-10 bg-white px-3 font-semibold border-b border-zinc-200 text-left",
                      compact ? "py-2 text-xs" : "py-3.5 text-sm",
                      x.sticky && "right-0",
                      {
                        "text-right": x.alignment === "right",
                        "text-center": x.alignment === "center",
                      },
                    )}
                  >
                    {x.title}
                  </th>
                ))}
              {config.rowActions && (
                <th className="sticky top-0 right-0 z-10 bg-white border-b border-l border-zinc-200 px-7 sm:w-12 sm:px-6" />
              )}
            </tr>
          </thead>
          <tbody className="bg-white">
            {visibleData &&
              visibleData.map((x, i, arr) => {
                return (
                  <tr
                    key={i}
                    role={config.onClick ? "button" : ""}
                    onClick={config.onClick ? () => config.onClick(x) : null}
                  >
                    {selectionEnabled && (
                      <td
                        className={cn(
                          "relative overflow-visible whitespace-nowrap bg-white px-3 py-2 border-zinc-200 border-b",
                          compact ? "text-xs" : "text-sm",
                          rowHeightClass,
                          noFooter && i === arr.length - 1 && "border-b-0",
                        )}
                        style={{ width: 40, minWidth: 40, maxWidth: 40 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Checkbox
                          checked={isRowSelected(x)}
                          disabled={
                            selectionDisabled ||
                            (isRowDisabled && isRowDisabled(x))
                          }
                          onChange={() => {
                            if (selectionDisabled) return;
                            onToggleRow(x);
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                    )}
                    {config.columns
                      .filter((x) => !x.shouldShow || x.shouldShow())
                      .map((y, j) => {
                        const cellContent = y.render(x);
                        const title = extractTitle(cellContent);
                        return (
                          <td
                            key={j}
                            title={title}
                            className={cn(
                              "relative overflow-visible whitespace-nowrap bg-white px-3 py-2 border-zinc-200 border-b",
                              compact ? "text-xs" : "text-sm",
                              rowHeightClass,
                              noFooter && i === arr.length - 1 && "border-b-0",
                              y.sticky && "sticky right-0 border-l",
                              {
                                "text-right": y.alignment === "right",
                                "text-center": y.alignment === "center",
                              },
                            )}
                            style={{
                              maxWidth: y.width || "200px",
                              minWidth: y.width || "200px",
                              overflowX: "hidden",
                              textOverflow: "ellipsis",
                              paddingLeft: noPad ? "0" : "",
                              paddingRight: noPad ? "0" : "",
                            }}
                          >
                            {cellContent}
                          </td>
                        );
                      })}
                    {config.rowActions && (
                      <td
                        className={cn(
                          "bg-white px-3 text-sm border-b border-l border-zinc-200 sticky right-0",
                          rowHeightClass,
                        )}
                      >
                        <div className="flex gap-2">
                          {config.rowActions
                            .filter(
                              (y) =>
                                (!y.show || y.show(x)) &&
                                (!y.permission || hasPermission(y.permission)),
                            )
                            .map((y, i) => (
                              <Button
                                key={i}
                                color="link"
                                className="whitespace-nowrap"
                                onClick={() => y.onClick(x)}
                                title={y.title}
                                aria-label={y.title}
                              >
                                {y.icon && <y.icon className="me-1" />}
                                {y.label}
                              </Button>
                            ))}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            {hasMore && (
              <tr ref={sentinelRef}>
                <td
                  colSpan={
                    config.columns.filter(
                      (x) => !x.shouldShow || x.shouldShow(),
                    ).length +
                    (selectionEnabled ? 1 : 0) +
                    (config.rowActions ? 1 : 0)
                  }
                  className="py-4 text-center text-sm text-zinc-400"
                >
                  Loading more…
                </td>
              </tr>
            )}
            {!noFooter && (
              <tr>
                <td
                  colSpan={
                    config.columns.length +
                    (extraCol ? 1 : 0) +
                    (selectionEnabled ? 1 : 0)
                  }
                  style={{
                    paddingLeft: noPad ? "0" : "",
                    paddingRight: noPad ? "0" : "",
                  }}
                  className={cn(
                    "sticky z-10 bottom-0 py-4 text-sm bg-white",
                    noPad ? "px-0" : "px-3",
                  )}
                >
                  <div className="flex justify-between">
                    <div>
                      {data && data.length} results
                      {hasMore && ` (showing ${visibleCount})`}
                    </div>
                    {enableExport && exportPosition === "bottom" && data && data.length > 0 && (
                      <CSVLink
                        data={data}
                        headers={headers()}
                        filename={`${config.key || "export"}_${new Date().getTime()}.csv`}
                        target="_blank"
                      >
                        <Button color="secondary">
                          <Download className="mr-2" />
                          Export
                        </Button>
                      </CSVLink>
                    )}
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SimpleTable;
